import { PATH } from '../../routes';
import { removeNumberFormatting } from './reducer';

export type GivingPartnerType = 'church' | 'nonprofit' | undefined;

export interface OrganizationSuggestionsResponse {
    index?: string;
    type?: string;
    id?: string;
    score?: number;
    source: OrganizationSuggestions;
    nameDistanceScore?: number;
    pastorDistanceScore?: number;
    nameExactScore?: number;
    totalScore?: number;
}

export interface OrganizationSuggestions {
    zip: string;
    address: string;
    city: string;
    official: string;
    doneeId: string;
    doneeType: GivingPartnerType;
    spelledState: string;
    nameNoLocation: string;
    extId: string;
    bgaLink: string;
    phone: string;
    name: string;
    logo: string;
    photo: string;
    pastorAvatar: string;
    state: string;
    unregistered: '0' | '1';
    lat: string;
    lon: string;
    coords: string;
    ypId: string;
    resultType: string;
    country?: string;
}

export const defaultOrganizationSuggestion: OrganizationSuggestions = {
    spelledState: '',
    nameNoLocation: '',
    extId: '',
    bgaLink: '',
    logo: '',
    photo: '',
    pastorAvatar: '',
    unregistered: '0',
    lat: '',
    lon: '',
    coords: '',
    ypId: '',
    resultType: '',
    address: '',
    city: '',
    doneeId: '',
    doneeType: undefined,
    name: '',
    official: '',
    phone: '',
    state: '',
    zip: '',
};

export interface LocationSelected {
    userTypedValue: string;
    city: string;
    state: string;
    zip: string;
}

export type Country = 'us' | 'bs';

export interface Organization {
    organizationName: string;
    organizationLocation: LocationSelected;
    organizationPhoneNumber: string;
    organizationZip: string;
    organizationAddress: string;
    organizationCity: string;
    organizationState: string;
    doneeId: number;
    doneeType: OrganizationSuggestions['doneeType'];
    ypId: string;
    lat: string;
    lon: string;
    organizationCountry?: Country;
}

export const defaultOrganization: Organization = {
    organizationName: '',
    organizationLocation: {
        userTypedValue: '',
        city: '',
        state: '',
        zip: '',
    },
    organizationPhoneNumber: '',
    organizationZip: '',
    organizationAddress: '',
    organizationCity: '',
    organizationState: '',
    doneeId: 0,
    doneeType: 'church',
    ypId: '',
    lat: '',
    lon: '',
};

export interface UTMQueryString {
    utmCampaign?: string;
    queryString?: string;
    utmMedium?: string;
    utmSource?: string;
}

export interface SignupState extends Organization, UTMQueryString {
    uuid: string;
    userTitle: string;
    userFirstName: string;
    userLastName: string;
    userEmail: string;
    userPhone: string;
    userPassword: string;
    ein: string;
    ypId: string;
    userTitles: string[];
    fingerprint: string;
    selectedOrganization: OrganizationSuggestionsResponse | null;
}

export const userEmailApiText = 'officialEmail';

export const signupApiDto = (
    signup: SignupState & { recaptcha?: unknown; country?: Country },
): object => ({
    uuid: signup.uuid,
    doneeType: signup.doneeType || 'church', // fall back value
    phone: removeNumberFormatting(signup.organizationPhoneNumber),
    organizationName: signup.organizationName,
    address: signup.organizationAddress,
    postal:
        signup.organizationCountry === 'us'
            ? signup.organizationZip
            : undefined,
    city: signup.organizationCity,
    state: signup.organizationState,
    taxId: removeNumberFormatting(signup.ein),
    officialFirst: signup.userFirstName,
    officialLast: signup.userLastName,
    officialPhone: removeNumberFormatting(signup.userPhone),
    officialTitle: signup.userTitle,
    [userEmailApiText]: signup.userEmail,
    utmCampaign: signup.utmCampaign,
    queryString: signup.queryString,
    utmMedium: signup.utmMedium,
    utmSource: signup.utmSource,
    password: signup.userPassword,
    recaptcha: signup.recaptcha,
    ypId: signup.ypId,
    lat: signup.lat,
    lon: signup.lon,
    country: signup.organizationCountry === 'us' ? 'United States' : 'BHS',
    userKey: signup.fingerprint,
});

// it should be in sync with `ALL_REQUIRED_STEPS`
const getRequiredSignupKeys = (country: Country): (keyof SignupState)[] => {
    const requiredKeys: (keyof SignupState)[] = [
        'organizationName',
        'organizationPhoneNumber',
        'organizationAddress',
        'userTitle',
        'userFirstName',
        'userLastName',
        'userEmail',
        'userPhone',
    ];

    if (country === 'us') {
        requiredKeys.push('organizationZip');
    }

    return requiredKeys;
};

const BAHAMAS_REQUIRED_FIELDS: (keyof SignupState)[] = [
    'organizationName',
    'organizationPhoneNumber',
    'organizationAddress',
];

const USA_REQUIRED_FIELDS: (keyof SignupState)[] = [
    'organizationName',
    'organizationPhoneNumber',
    'organizationZip',
    'organizationAddress',
];

// This is not the only source of truth, refer to the  individual steps
const getRequiredSteps = (country: Country): MissingStepsType[] => {
    return [
        {
            title: 'Step 1',
            route: PATH.SIGNUP.STEPS.ONE,
            keys:
                country === 'us'
                    ? USA_REQUIRED_FIELDS
                    : BAHAMAS_REQUIRED_FIELDS,
        },
        {
            title: 'Step 2',
            route: PATH.SIGNUP.STEPS.TWO,
            keys: ['userTitle'],
        },
        {
            title: 'Step 3',
            route: PATH.SIGNUP.STEPS.THREE,
            keys: ['userFirstName', 'userLastName'],
        },
        {
            title: 'Step 4',
            route: PATH.SIGNUP.STEPS.FOUR,
            keys: ['userEmail'],
        },
        {
            title: 'Step 5',
            route: PATH.SIGNUP.STEPS.FIVE,
            keys: ['userPhone'],
        },
    ];
};

export interface MissingStepsType {
    title: string;
    route: string;
    keys: (keyof SignupState)[];
}

export const whichStepsAreMissing = (
    state: SignupState,
): MissingStepsType[] => {
    const requiredSteps = getRequiredSteps(state.organizationCountry || 'us');
    const missingSteps = [];

    for (const step of requiredSteps) {
        for (const key of step.keys) {
            if (!state[key]) {
                missingSteps.push(step);
                break;
            }
        }
    }
    return missingSteps;
};

export const calculatePercentage = (state: SignupState): number => {
    let totalCompletedValues = 0;
    const requiredKeys = getRequiredSignupKeys(
        state.organizationCountry || 'us',
    );
    for (const value of requiredKeys) {
        if (state[value]) {
            totalCompletedValues += 1;
        }
    }

    return (totalCompletedValues / requiredKeys.length) * 100;
};

export const SIGNUP_SET = 'SIGNUP_SET_ACTION';

export interface SignupSetAction {
    type: typeof SIGNUP_SET;
    signup: Partial<SignupState>;
}

export const SET_OFFICER_TITLES = 'SET_OFFICER_TITLES';
export interface SetOfficerTitles {
    type: typeof SET_OFFICER_TITLES;
    data: string[];
}

export const SET_FINGERPRINT = 'SET_FINGERPRINT';
export interface SetFingerprintAction {
    type: typeof SET_FINGERPRINT;
    fingerprint: string;
}

export type SignupActions =
    | SignupSetAction
    | SetOfficerTitles
    | SetFingerprintAction;
